import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { KeyRound } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';

export function ProfilePage() {
  const { employee, authMode } = useAuth();
  const passwordEnabled = authMode === 'password' || authMode === 'both';

  const [currentPassword, setCurrentPassword] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');

  const change = useMutation({
    mutationFn: async () => {
      if (newPassword.length < 8) throw new Error('New password must be at least 8 characters.');
      if (newPassword !== confirm) throw new Error('New password and confirmation do not match.');
      const r = await api('/auth/me/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (!r.success) throw new Error(r.error?.message || 'Failed');
      return r.data;
    },
    onSuccess: () => {
      toast('Password changed', undefined, 'success');
      setCurrentPassword(''); setNewPassword(''); setConfirm('');
    },
    onError: (e: any) => toast('Error', e.message, 'error'),
  });

  return (
    <div className="space-y-4 max-w-2xl">
      <h1 className="text-2xl font-semibold">Profile</h1>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Account</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Row label="Full Name" value={employee?.fullName ?? '—'} />
          <Row label="Email" value={employee?.email ?? '—'} />
          <Row label="Role" value={employee?.rolePermission?.name ?? '—'} />
        </CardContent>
      </Card>

      {passwordEnabled && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <KeyRound className="h-4 w-4" /> Change Password
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Current Password</Label>
              <Input
                type="password" autoComplete="current-password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">Leave blank if you have never set a password (e.g. magic-link only account).</p>
            </div>
            <div>
              <Label>New Password</Label>
              <Input
                type="password" autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div>
              <Label>Confirm New Password</Label>
              <Input
                type="password" autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <div className="flex justify-end">
              <Button
                onClick={() => change.mutate()}
                disabled={change.isPending || !newPassword || !confirm}
              >
                Change Password
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {!passwordEnabled && (
        <Card>
          <CardContent className="py-4 text-sm text-muted-foreground">
            Password login is disabled for this app (auth mode: <span className="font-medium">{authMode}</span>).
            Sign in is via a one-time Link sent to your email only.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[180px_1fr] gap-3 py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
